import React from "react";
import { DocumentNode, gql, useQuery } from "@apollo/client";
import { pipe } from "fp-ts/function";
import * as Ap from "fp-ts/Apply";
import * as E from "fp-ts/Either";
import * as O from "fp-ts/Option";
import { PathReporter } from "io-ts/lib/PathReporter";
import * as Sentry from "@sentry/react";
import { match } from "ts-pattern";

import { Loading } from "src/components/loading/loading";
import { Error } from "src/components/error/error";
import { Report, ReportEdge, School, Viewer } from "src/generated/types";

import { ReportPartialPage as V1FSCIPartial } from "./fsci/v1/report_partial_page";
import { ReportPartialPage as V2FSCIPartial } from "./fsci/v2/report_partial_page";
import { ReportPartialPage as AllFSCIPartial } from "./fsci/all/report_partial_page";
import { FSCIBrandedV1Report as V1FSCIReport, V1ReportC as V1FSCIReportC } from "./fsci/v1/types";
import { FSCIBrandedV2Report as V2FSCIReport, V2ReportC as V2FSCIReportC } from "./fsci/v2/types";
import { ReportPartialPage as V1FFIPartial } from "./ffi/v1/report_partial_page";
import { ReportPartialPage as AllFFIPartial } from "./ffi/all/report_partial_page";
import { FFIBrandedV1Report as V1FFIReport, V1ReportC as V1FFIReportC } from "./ffi/v1/types";

import "./report.scss";

/*
 * GraphQL
 */

export const REPORT_DETAIL_QUERY = gql`
    query ReportDetail($id: ID!) {
        viewer {
            user {
                id
                school {
                    id
                    name
                }
            }
        }
        report(id: $id) {
            id
            results
            version
            year
            type
            constituentGroups
            surveysCompleted
        }
    }
`;

export const REPORTS_DETAIL_QUERY = (type: string): DocumentNode => {
    let version = "2";

    if (type === "ffi") {
        version = "1";
    }

    return gql`
    query ReportsDetail($type: String!) {
        viewer {
            user {
                school {
                    id
                    name
                    reports(
                        where: { results: { exists: true }, version: { equalTo: "${version}" }, type: { equalTo: $type } }
                        order: [year_DESC]
                    ) {
                        edges {
                            node {
                                id
                                results
                                year
                                type
                                constituentGroups
                                surveysCompleted
                            }
                        }
                    }
                }
            }
        }
    }
`;
};
/*
 * Types
 */

interface SingleReportVars {
    readonly id: string;
}

interface MultiReportVars {
    readonly type: string;
}

interface ApolloQueryReportData {
    readonly viewer: Viewer;
    readonly report: Report;
}

interface ApolloQueryReportsData {
    readonly viewer: Viewer;
    readonly reports: Array<Report>;
}

interface ReportDetailPageProps {
    readonly id: string;
}

interface ReportMe {
    readonly message: string;
    readonly data: Array<string>;
}

/*
 * Main
 */

const reportDecoder = (report: Report): E.Either<ReportMe, V1FSCIReport | V2FSCIReport | V1FFIReport> => {
    if (report.version === "1" && report.type === "fsci") {
        const decodedReport = V1FSCIReportC.decode(report);

        return pipe(
            decodedReport,
            E.map(
                (results): V1FSCIReport => ({
                    tag: "v1_FSCI",
                    value: results,
                }),
            ),
            E.mapLeft(() => ({
                message: "Error decoding v1 FSCI report from server.",
                data: PathReporter.report(decodedReport),
            })),
        );
    } else if (report.version === "2" && report.type === "fsci") {
        const decodedReport = V2FSCIReportC.decode(report);

        return pipe(
            decodedReport,
            E.map(
                (results): V2FSCIReport => ({
                    tag: "v2_FSCI",
                    value: results,
                }),
            ),
            E.mapLeft(() => ({
                message: "Error decoding v2 FSCI report from server.",
                data: PathReporter.report(decodedReport),
            })),
        );
    } else if (report.version === "1" && report.type === "ffi") {
        const decodedReport = V1FFIReportC.decode(report);
        return pipe(
            decodedReport,
            E.map(
                (results): V1FFIReport => ({
                    tag: "v1_FFI",
                    value: results,
                }),
            ),
            E.mapLeft(() => ({
                message: "Error decoding v1 FFI report from server.",
                data: PathReporter.report(decodedReport),
            })),
        );
    } else {
        return E.left({
            message: "Unknown report version.",
            data: [`Found: V${report.version}: ${report.type}`],
        });
    }
};

export const ReportPartialMatcher = (props: ReportDetailPageProps): React.ReactElement => {
    const { id } = props;

    if (id !== "viewAllFSCI" && id !== "viewAllFFI") {
        const { data, loading, error } = useQuery<ApolloQueryReportData, SingleReportVars>(REPORT_DETAIL_QUERY, {
            variables: { id: id },
        });

        if (loading) return <Loading />;
        if (error) return <Error />;

        return pipe(
            Ap.sequenceT(O.Apply)(O.fromNullable(data?.report), O.fromNullable(data?.viewer.user.school)),
            O.fold(
                () => (
                    <div className="report">
                        <Error message="There is a problem with your account. Please contact an Admin so they can resolve the issue." />
                    </div>
                ),
                ([report, school]) => {
                    // Guard against reports with empty or missing results data
                    if (!report.results || Object.keys(report.results).length === 0) {
                        return (
                            <div className="report">
                                <Error message="Report data is not yet available for this period." />
                            </div>
                        );
                    }

                    return pipe(
                        reportDecoder(report),
                        E.fold(
                            (err) => {
                                console.warn(err);
                                if (process.env.NODE_ENV === "production") {
                                    Sentry.captureException(err);
                                }

                                return (
                                    <div className="report">
                                        <Error message="There is a problem with this report. Please contact an Admin so they can resolve the issue." />
                                    </div>
                                );
                            },
                            (brandedReport) => {
                                return match(brandedReport)
                                    .with({ tag: "v1_FSCI" }, (v1Report) => (
                                        <V1FSCIPartial report={v1Report} school={school} />
                                    ))
                                    .with({ tag: "v2_FSCI" }, (v2Report) => (
                                        <V2FSCIPartial report={v2Report} school={school} />
                                    ))
                                    .with({ tag: "v1_FFI" }, (v1Report) => (
                                        <V1FFIPartial report={v1Report} school={school} />
                                    ))
                                    .exhaustive();
                            },
                        ),
                    );
                },
            ),
        );
    } else {
        let variables: MultiReportVars = { type: "" };

        if (id === "viewAllFSCI") {
            variables = { type: "fsci" };
        } else if (id === "viewAllFFI") {
            variables = { type: "ffi" };
        }

        const { data, loading, error } = useQuery<ApolloQueryReportsData, MultiReportVars>(
            REPORTS_DETAIL_QUERY(variables.type),
            {
                variables,
            },
        );

        const reports = data?.viewer?.user?.school?.reports?.edges as Array<ReportEdge>;
        const school = data?.viewer?.user?.school as School;

        if (loading) return <Loading />;
        if (error) return <Error />;

        return id === "viewAllFSCI" ? (
            <AllFSCIPartial reports={reports} school={school} />
        ) : (
            <AllFFIPartial reports={reports} school={school} />
        );
    }
};
